import React from 'react';
import { HStack, Text, VStack } from '@gluestack-ui/themed';

type TextProps = React.ComponentProps<typeof Text>;
type StackSpace = React.ComponentProps<typeof VStack>['space'];

const SELECTABLE_WEB_SX = { _web: { userSelect: 'text' as const } };

export type BulletItemProps = {
  /**
   * The item text, or a custom line. A custom line must fill the row
   * (`flex={1}`) so wrapped lines stay aligned under the text, not the dot.
   */
  children: React.ReactNode;
  size?: TextProps['size'];
  color?: TextProps['color'];
  /** Let web users select the dot together with selectable item text. */
  selectable?: boolean;
  testID?: string;
};

/**
 * BulletItem
 * ----------
 * One bulleted line with a hanging indent: the dot sits in its own column,
 * so a wrapped line starts under the text.
 */
export function BulletItem({
  children,
  size,
  color,
  selectable = false,
  testID,
}: BulletItemProps) {
  return (
    <HStack space="xs" alignItems="flex-start" testID={testID}>
      <Text
        size={size}
        color={color}
        selectable={selectable}
        sx={selectable ? SELECTABLE_WEB_SX : undefined}
      >
        •
      </Text>
      {typeof children === 'string' ? (
        <Text flex={1} size={size} color={color}>
          {children}
        </Text>
      ) : children}
    </HStack>
  );
}

export type BulletListProps = {
  items: string[];
  size?: TextProps['size'];
  color?: TextProps['color'];
  space?: StackSpace;
  testID?: string;
};

/**
 * BulletList
 * ----------
 * A plain bulleted list of sentences. Use this instead of prefixing a
 * string with "•", which loses the hanging indent when the line wraps.
 */
export function BulletList({
  items,
  size,
  color,
  space = 'xs',
  testID,
}: BulletListProps) {
  return (
    <VStack space={space} testID={testID}>
      {items.map((item) => (
        <BulletItem key={item} size={size} color={color}>
          {item}
        </BulletItem>
      ))}
    </VStack>
  );
}
